import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useImmerReducer } from "use-immer";
import { toast } from "react-toastify";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import useProjectContext from "@/services/context/project/use-project-context.ts";
import useModelingToolsContext from "@/services/context/viewer/modeling-tools/use-modeling-tools-context.ts";
import useProjectOrganisationContext from "@/pages/takeoff/context/project-organisation/use-project-organisation-context.ts";
import ENDPOINTS, { ORIGIN_ENDPOINTS, BAAMER_FILE_REVISION } from "@/services/endpoints.ts";

import { putItem } from "@/queries/queries-helper.ts";

import { ProjectFilePlanContext, ProjectFilePlanContextProps } from "./project-file-plan-context-props.ts";
import ProjectFilePlanReducerAction from "./project-file-plan-reducer-action.ts";
import ProjectFileReducerAction from "./project-file-reducer-action.ts";
import projectFilePlanReducer from "./project-file-plan-reducer.ts";
import projectFileReducer from "./project-file-reducer.ts";

import OriginDto from "@/models/interfaces/dtos/origin-dto.ts";
import BaseFileData from "@/pages/takeoff/context/project-organisation/models/base-file-data.ts";
import PlanFileData from "@/pages/takeoff/context/project-measure/models/plan-file-data.ts";
import PlanOrigin from "@/pages/takeoff/context/project-organisation/models/plan-origin.ts";
import FileType from "@/models/enums/file-type.ts";

interface ProjectFilePlanContextProviderProps extends React.PropsWithChildren {
    fileIdLeft: string;
    fileIdRight: string | undefined;
    setFileIdRight: (id: string | undefined) => void;
}

const ProjectFilePlanContextProvider: React.FC<ProjectFilePlanContextProviderProps> = ({ fileIdLeft, fileIdRight, setFileIdRight, children }) => {
    const { ensureCanEdit, canEdit } = useProjectEditAccess();
    const { project } = useProjectContext();

    const { setIsInDrawingMode } = useModelingToolsContext();

    const { data: projectData, getAllPlanFilesData, getPlanFileData, getAll3dFilesData, getBaamerFileRevisionIdFromStoreyId, getBaamerFileRevisionIdFromFileId, invalidateProjectReferences } = useProjectOrganisationContext();

    const { building, storey: storeyLeft, file: fileLeft } = useMemo(() => {
        if (projectData == null) return undefined;
        return projectData.findBuildingStoreyAndFileFromFileId(fileIdLeft);
    }, [fileIdLeft, projectData]) ?? { storeyLeft: undefined, fileLeft: undefined };

    const { storey: storeyRight, file: fileRight } = useMemo(() => {
        if (projectData == null || fileIdRight == null) return undefined;
        
        // Cas fichier 2d
        const found = projectData.findBuildingStoreyAndFileFromFileId(fileIdRight);
        if (found)
            return found;

        // Cas fichier 3d
        const _3dFileData = getAll3dFilesData().find(f => f.id === fileIdRight);
            if (_3dFileData)
                return { storey: undefined, file: BaseFileData.createFromFileData3D(_3dFileData) };

        return undefined;
    }, [fileIdRight, projectData, getAll3dFilesData]) ?? { storeyRight: undefined, fileRight: undefined };
    
    const storeyId = storeyLeft?.id;

    const [isSaving, setIsSaving] = useState(false);

    const [isOverlayActive, setIsOverlayActive] = useState(false);
    
    const planFileDataLeft = useMemo(() => {
        if (!storeyId || !fileIdLeft) return null;
        return getPlanFileData(storeyId, fileIdLeft)?.clone() ?? null;
    }, [storeyId, fileIdLeft, getPlanFileData]);
    const [dataLeft, dispatchLeft] = useImmerReducer<PlanFileData, ProjectFilePlanReducerAction>(
        projectFilePlanReducer,
        planFileDataLeft ?? new PlanFileData()
    );
    useEffect(() => {
        if (!planFileDataLeft) {
            dispatchLeft({
                type: "reset",
                data: new PlanFileData()
            });

            return;
        }

        dispatchLeft({
            type: "reset",
            data: planFileDataLeft
        });
    }, [fileIdLeft, storeyId, planFileDataLeft, dispatchLeft]);

    useEffect(() => {
        if (!dataLeft || !planFileDataLeft) return;

        if (dataLeft.origins.length === 0 || dataLeft.isDirty) return;

        const activeOrigin = dataLeft.origins.find(o => o.isActive);

        setIsOverlayActive(activeOrigin !== undefined);

        if (!activeOrigin?.relativeOrigineId) return;

        const file2d = getAllPlanFilesData()
            .find(fsd =>
                fsd.data.origins.some(o => o.id === activeOrigin.relativeOrigineId)
            );

        if (file2d) {
            setFileIdRight(file2d.fileId);
            return;
        }

        const file3d = getAll3dFilesData()
            .find(f => f.origins.some(o => o.id === activeOrigin.relativeOrigineId));

        if (file3d) {
            setFileIdRight(file3d.id);
        }
    }, [
        dataLeft,
        planFileDataLeft,
        getAllPlanFilesData,
        getAll3dFilesData,
        setFileIdRight
    ]);

    const [defaultActiveFileDataRight, setDefaultActiveFileDataRight] = useState<PlanOrigin | null>(null);

    const fileDataRight = useMemo(() => {
        if (!fileIdRight) return null;
        
        // Cas fichier 2d
        if (storeyRight) {
            const planFileData = getPlanFileData(storeyRight.id, fileIdRight)?.clone() ?? null;
            if (planFileData) {
                const baseFile = BaseFileData.createFromBaseFileData(fileRight!);
                baseFile.origin = planFileData.origin;
                baseFile.origins = planFileData.origins;
                baseFile.content = planFileData.content;
                return baseFile;
            }
        }
        // Cas fichier 3d
        else {
            const _3dFileData = getAll3dFilesData().find(f => f.id === fileIdRight)?.clone() ?? null;
            if (_3dFileData)
                return BaseFileData.createFromFileData3D(_3dFileData);
        }
        return null;
    }, [storeyRight, fileRight, fileIdRight, getPlanFileData, getAll3dFilesData]);
    const [dataRight, dispatchRight] = useImmerReducer<BaseFileData, ProjectFileReducerAction>(
        projectFileReducer,
        fileDataRight ?? new BaseFileData("", "", FileType.File3D, 0, "", "") // Initialisation bidon,
    );
    useEffect(() => {
        if (!fileIdRight || !fileDataRight) return;

        dispatchRight({
            type: 'reset',
            data: fileDataRight
        });        
    }, [fileIdRight, fileDataRight, dispatchRight]);
    useEffect(() => {
        if (!fileDataRight) return;
        setDefaultActiveFileDataRight(fileDataRight.origin);
    }, [fileDataRight, setDefaultActiveFileDataRight]);

    useEffect(() => {
        if (!isOverlayActive) return;
        if (!dataLeft || dataLeft.origins.length === 0) return;
        if (!dataRight || dataRight.origins.length === 0) return;

        const activeOriginLeft = dataLeft.origins.find(o => o.isActive);
        if (!activeOriginLeft?.relativeOrigineId) return;

        const originRight = dataRight.origins.find(o => o.id === activeOriginLeft.relativeOrigineId);
        if (originRight) {
            dispatchRight({
                type: 'set-origin',
                originId: originRight.id
            });
        }
    }, [isOverlayActive, dataLeft, dataRight, dispatchRight]);

    const urlFileLeft = useMemo(() => ENDPOINTS.VIEWER_PDF_PREVIEW(project.id, `${project.projectOrganisation.id}/${project.id}/${fileLeft?.relativePath}`), [project.projectOrganisation.id, project.id, fileLeft?.relativePath]);

    const urlFileRight = useMemo(() => {
        if (fileRight?.relativePath.toLocaleLowerCase().endsWith(".pdf"))
            return ENDPOINTS.VIEWER_PDF_PREVIEW(project.id, `${project.projectOrganisation.id}/${project.id}/${fileRight?.relativePath}`);
        if (fileRight?.relativePath.toLocaleLowerCase().endsWith(".ifc"))
            return ENDPOINTS.VIEWER_FILE_SAAS(`${project.projectOrganisation.id}/${project.id}/${fileRight?.relativePath}`);
        return undefined;
    }, [project.projectOrganisation.id, project.id, fileRight?.relativePath]);

    const [isDrawingLeft, setIsDrawingLeft] = useState<boolean>(false);

    const [isDrawingRight, setIsDrawingRight] = useState<boolean>(false);

    useEffect(() => {
        setIsInDrawingMode(isDrawingLeft || isDrawingRight);
    }, [isDrawingLeft, isDrawingRight, setIsInDrawingMode]);

    const dispatchLeftEdit = useCallback((action: Parameters<typeof dispatchLeft>[0]) => {
        if (ensureCanEdit()) {
            dispatchLeft(action);
        }
    }, [ensureCanEdit, dispatchLeft]);

    const dispatchRightEdit = useCallback((action: Parameters<typeof dispatchRight>[0]) => {
        if (ensureCanEdit()) {
            dispatchRight(action);
        }
    }, [ensureCanEdit, dispatchRight]);

    useEffect(() => {
        if (canEdit) return;
        setIsDrawingLeft(false);
        setIsDrawingRight(false);
        setIsInDrawingMode(false);
    }, [canEdit, setIsInDrawingMode]);

    const saveCurrentFileData = useCallback(async (): Promise<boolean>  => {
        if (!ensureCanEdit()) {
            return false;
        }

        if (storeyId == null) return false;

        setIsSaving(true);

        try {
            const baamerFileRevisionIdLeft = getBaamerFileRevisionIdFromStoreyId(storeyId);
            if (baamerFileRevisionIdLeft == null) return false;
            
            const originsLeft: OriginDto[] = [];
            // const relativeOriginId: string | undefined = dataRight.origins.find(o => o.isActive)?.id;
            const relativeOriginId: string | undefined = dataLeft.origin.relativeOrigineId;

            if (isOverlayActive && !dataLeft.origins.find(o => o.isActive)) {
                toast.warning("Vous devez d'abord définir une origine.", { autoClose: false });
                return false;
            }

            if (isOverlayActive && !relativeOriginId) {
                toast.warning("Vous devez d'abord définir une origine sur votre plan ou 3D de référence.", { autoClose: false });
                return false;
            }

            dataLeft.origins.forEach(o => {
                originsLeft.push({ 
                    originId: o.id, 
                    baamerFileRevisionId: baamerFileRevisionIdLeft, 
                    relativeOriginId: o.isActive ? relativeOriginId : o.relativeOrigineId,
                    isActive: o.isActive, 
                    name: o.name, 
                    color: o.color, 
                    position: JSON.stringify(o.position),
                    direction: JSON.stringify(o.direction)
                })
            });

            const originsRight: OriginDto[] = [];
            if (dataRight && dataRight.origin.name !== "Origine par défaut" && isOverlayActive) {
                const baamerFileRevisionIdRight = getBaamerFileRevisionIdFromFileId(dataRight.id);
                
                dataRight.origins.forEach(o => {
                    const origin: OriginDto = { 
                        originId: o.id, 
                        baamerFileRevisionId: baamerFileRevisionIdRight!,
                        isActive: o.id === defaultActiveFileDataRight?.id, 
                        name: o.name, 
                        color: o.color, 
                        position: JSON.stringify(o.position),
                        direction: JSON.stringify(o.direction)
                    };
                    if (origin.isActive) {
                        origin.relativeOriginId = defaultActiveFileDataRight?.relativeOrigineId;
                    }
                    originsRight.push(origin);
                });

                await putItem<OriginDto[]>(ORIGIN_ENDPOINTS.UPDATE(baamerFileRevisionIdRight!), originsRight)
                    .then(async () => {
                        await putItem<OriginDto[]>(ORIGIN_ENDPOINTS.UPDATE(baamerFileRevisionIdLeft), originsLeft)
                            .then(async () => {
                                await putItem<boolean>(BAAMER_FILE_REVISION.UPDATE_ORIGINS_DATA(project.baamerRevisionId), [])
                                    .then(async () => {
                                        await invalidateProjectReferences();

                                        dispatchRight({
                                            type: 'set-origin',
                                            originId: dataRight.origin.id
                                        });

                                        dispatchLeft({
                                            type: 'set-dirty-flag',
                                            isDirty: false
                                        });

                                        dispatchRight({
                                            type: 'set-dirty-flag',
                                            isDirty: false
                                        });
                                        
                                        toast.success('Sauvegarde réussie');
                                    });
                            });
                    });
            }
            else {
                await putItem<OriginDto[]>(ORIGIN_ENDPOINTS.UPDATE(baamerFileRevisionIdLeft), originsLeft)
                    .then(async () => {
                        await putItem<boolean>(BAAMER_FILE_REVISION.UPDATE_ORIGINS_DATA(project.baamerRevisionId), [])
                         .then(async () => {
                            await invalidateProjectReferences();

                            dispatchLeft({
                                type: 'set-dirty-flag',
                                isDirty: false
                            });
                            dispatchRight({
                                type: 'set-dirty-flag',
                                isDirty: false
                            });
                            
                            toast.success('Sauvegarde réussie');
                        });
                    });
            }
        } catch (error) {
            if (error instanceof Error)
                toast.error(error.message, {autoClose: false});
            return false;
        } finally {
            setIsSaving(false);
        }
        
        return true;
    }, [ensureCanEdit, project.baamerRevisionId, storeyId, dataLeft, dataRight, defaultActiveFileDataRight, isOverlayActive, dispatchLeft, dispatchRight, invalidateProjectReferences, getBaamerFileRevisionIdFromStoreyId, getBaamerFileRevisionIdFromFileId, setIsSaving]);
    
    const props: ProjectFilePlanContextProps = useMemo(() => ({
        isOverlayActive,
        setIsOverlayActive,

        planFileDataLeft: dataLeft,
        fileDataRight: dataRight,
        
        urlFileLeft,
        urlFileRight,

        building,
        storey: storeyLeft,
        
        fileLeft,
        fileRight,
        
        isDirty: dataLeft.isDirty || dataRight.isDirty,
        isSaving,

        setFileIdRight,

        isDrawingLeft,
        setIsDrawingLeft,
        isDrawingRight,
        setIsDrawingRight,

        dispatchLeft: dispatchLeftEdit,
        dispatchRight: dispatchRightEdit,

        saveCurrentFileData,
    }), [isOverlayActive, setIsOverlayActive, dataLeft, dataRight, urlFileLeft, urlFileRight, building, storeyLeft, fileLeft, fileRight, isSaving, setFileIdRight, isDrawingLeft, setIsDrawingLeft, isDrawingRight, setIsDrawingRight, dispatchLeftEdit, dispatchRightEdit, saveCurrentFileData]);
    
    return (
        <ProjectFilePlanContext.Provider value={props}>
            {children}
        </ProjectFilePlanContext.Provider>
    )
}

export default ProjectFilePlanContextProvider;